import React, { useState, useEffect, useCallback } from 'react'
import { Box, Button } from '@mui/material'

/* ⛔ LA VISITE GUIDEE DE LA PREMIERE CONNEXION (09/09/2026).
   Demande d'Hicham : « lors de la premiere connexion, de petites explications
   sur les boutons ».

   Trois partis pris, et leurs raisons :

   1. LES CIBLES SONT DESIGNEES PAR UN ATTRIBUT, pas par une position ni par
      une classe. `data-visite="reprendre"` survit a une refonte de la mise en
      page ; une classe MUI generee (`css-1c8qkno`) change au prochain build.

   2. UNE ETAPE DONT LA CIBLE MANQUE EST SAUTEE, en silence. Le tableau de bord
      n'affiche pas les memes boutons selon l'avancement : « Commencer »
      n'existe pas pour qui a deja commence. Une visite qui pointe le vide est
      pire que pas de visite.

   3. ON MEMORISE DANS LE NAVIGATEUR, volontairement. Une colonne en base
      serait plus propre — la visite suivrait la personne d'une machine a
      l'autre — mais c'est une migration de production pour un confort. En v1
      on reste local, et le menu garde une entree pour revoir le guide.
      ⚠️ `localStorage` leve une exception en navigation privee au lieu de
         renvoyer null : tout acces est enrobe. */

const lu = (cle) => {
  try { return window.localStorage.getItem(cle) === "1" } catch (e) { return false }
}
const marquer = (cle) => {
  try { window.localStorage.setItem(cle, "1") } catch (e) { /* navigation privee */ }
}

export const visiteDejaFaite = lu
export const oublierVisite = (cle) => {
  try { window.localStorage.removeItem(cle) } catch (e) { /* navigation privee */ }
}

const MARGE = 8


/* La cible d'une etape : le premier element VISIBLE qui correspond (PD-17,
   02/10/2026). Le tableau de bord rend deux fois le bouton du module en
   cours, l'un masque selon la taille d'ecran ; le premier du document peut
   etre le masque. A defaut d'element visible (jsdom ne mesure rien), le
   premier tout court. */
const trouverCible = (selecteur) => {
  const tous = [...document.querySelectorAll(selecteur)]
  return tous.find((el) => el.getClientRects().length > 0) || tous[0] || null
}

const VisiteGuidee = ({ cle, etapes, actif = true, onFin }) => {
  const [pas, setPas] = useState(0)
  const [zone, setZone] = useState(null)
  const [ouverte, setOuverte] = useState(false)

  /* ⛔ ON ATTEND QUE LES CIBLES SOIENT LA, on ne PARIE pas sur un delai.
     Premiere version : un `setTimeout` de 900 ms, puis un filtrage une fois
     pour toutes. Mesure au banc : la visite s'ouvrait avec DEUX etapes sur
     trois, parce que le menu du haut n'etait pas encore rendu a cet instant.
     Un delai fixe est un pari sur la vitesse de la machine ; ici on regarde
     toutes les 300 ms jusqu'a ce que le compte soit stable, avec une sortie
     au bout de six secondes pour ne jamais bloquer. */
  useEffect(() => {
    if (!actif || !cle || lu(cle)) return
    let stop = false
    let dernier = -1, stables = 0, tours = 0
    const compter = () => {
      let n = 0
      for (const e of (etapes || [])) {
        try { if (trouverCible(e.cible)) n++ } catch (err) { /* selecteur invalide */ }
      }
      return n
    }
    const battre = () => {
      if (stop) return
      const n = compter()
      stables = (n === dernier && n > 0) ? stables + 1 : 0
      dernier = n
      tours++
      /* ⚠️ ON ATTEND LE COMPTE COMPLET, pas le premier compte stable.
         Mesure au banc : avec « stable une fois », la visite s'ouvrait avec
         DEUX cibles sur trois — le menu du haut arrive apres le corps de la
         page, et deux relevés consecutifs peuvent tres bien etre d'accord sur
         un compte partiel. On ouvre donc quand TOUTES les cibles sont la ;
         a defaut, quand le compte n'a plus bouge pendant 1,2 s ; et de toute
         facon au bout de six secondes, pour ne jamais rester ferme. */
      const total = (etapes || []).length
      if (n >= total || (n > 0 && stables >= 4) || tours > 20) { setOuverte(true); return }
      setTimeout(battre, 300)
    }
    const t = setTimeout(battre, 500)
    return () => { stop = true; clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actif, cle])

  /* Filtre a chaque rendu : une etape dont la cible disparait en cours de
     route ne doit pas rester dans le compte.
     ⛔ CE TABLEAU EST NEUF A CHAQUE RENDU. Il ne doit donc JAMAIS servir de
        dependance a un `useCallback` ou un `useEffect` : une premiere version
        le faisait, et la page partait en boucle infinie — l'effet se
        relancait, posait `zone`, ce qui re-rendait, ce qui recreait le
        tableau, ce qui relancait l'effet. Le banc s'est bloque net.
        Les dependances sont donc `pas` et `etapes` (la propriete d'origine,
        stable), et la lecture du tableau se fait DANS la fonction. */
  const etapesVisibles = (etapes || []).filter((e) => {
    try { return !!trouverCible(e.cible) } catch (err) { return false }
  })

  const placer = useCallback(() => {
    let liste = []
    try {
      liste = (etapes || []).filter((e) => !!trouverCible(e.cible))
    } catch (err) { liste = [] }
    const e = liste[pas]
    const el = e ? trouverCible(e.cible) : null
    if (!el) { setZone((z) => (z === null ? z : null)); return }
    const r = el.getBoundingClientRect()
    const neuf = { x: r.left, y: r.top, l: r.width, h: r.height }
    /* On ne repose l'etat que s'il a REELLEMENT change : un objet neuf a
       chaque appel suffirait a re-rendre sans fin. */
    setZone((z) => (z && z.x === neuf.x && z.y === neuf.y && z.l === neuf.l && z.h === neuf.h) ? z : neuf)
  }, [etapes, pas])

  useEffect(() => {
    if (!ouverte) return
    placer()
    window.addEventListener("resize", placer)
    window.addEventListener("scroll", placer, true)
    return () => {
      window.removeEventListener("resize", placer)
      window.removeEventListener("scroll", placer, true)
    }
  }, [ouverte, placer])

  const fermer = () => {
    setOuverte(false)
    if (cle) marquer(cle)
    if (onFin) onFin()
  }

  useEffect(() => {
    if (!ouverte) return
    const auClavier = (ev) => { if (ev.key === "Escape") fermer() }
    window.addEventListener("keydown", auClavier)
    return () => window.removeEventListener("keydown", auClavier)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouverte])

  if (!ouverte || etapesVisibles.length === 0) return null
  const etape = etapesVisibles[pas]
  if (!etape) return null

  const dernier = pas >= etapesVisibles.length - 1

  /* La bulle se pose SOUS la cible, ou au-dessus si elle deborderait du bas.
     Elle se recale horizontalement pour ne jamais sortir de la fenetre : une
     bulle a moitie hors de l'ecran ne s'excuse pas, elle ne se lit pas. */
  const largeurBulle = 320
  const enDessous = !zone || (zone.y + zone.h + 170 < window.innerHeight)
  const gauche = zone
    ? Math.max(16, Math.min(window.innerWidth - largeurBulle - 16,
        zone.x + zone.l / 2 - largeurBulle / 2))
    : Math.max(16, window.innerWidth / 2 - largeurBulle / 2)
  const haut = zone
    ? (enDessous ? zone.y + zone.h + 14 : Math.max(16, zone.y - 14 - 150))
    : window.innerHeight / 2 - 75

  return (
    <Box sx={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:2000 }}>
      {/* Le voile. On le rend cliquable pour fermer : quelqu'un qui clique a
          cote veut passer, pas rester prisonnier d'une bulle. */}
      <Box onClick={fermer} sx={{
        position:"absolute", top:0, left:0, right:0, bottom:0,
        background:"rgba(10,10,11,0.55)",
      }} />

      {/* Le halo autour de la cible : un cadre clair, sans masquer l'element.
          On ne perce pas le voile — un trou se decale d'un pixel a chaque
          changement de zoom, et le decalage se voit. Un cadre, lui, tient. */}
      {zone &&
        <Box sx={{
          position:"absolute",
          left: zone.x - MARGE, top: zone.y - MARGE,
          width: zone.l + MARGE * 2, height: zone.h + MARGE * 2,
          borderRadius:"8px",
          border:"2px solid #FFFFFF",
          boxShadow:"0 0 0 9999px rgba(10,10,11,0.55)",
          pointerEvents:"none",
        }} />
      }

      <Box sx={{
        position:"absolute",
        left: gauche + "px",
        top: haut + "px",
        width: largeurBulle + "px",
        background:"#FFFFFF",
        borderRadius:"10px",
        padding:"18px 20px",
        boxShadow:"0 10px 40px rgba(0,0,0,0.28)",
      }}>
        <Box sx={{ fontSize:"12px", color:"text.subTitle", marginBottom:"6px", userSelect:"none" }}>
          {(pas + 1) + " / " + etapesVisibles.length}
        </Box>
        <Box sx={{ fontSize:"17px", fontWeight:600, color:"text.black", lineHeight:"130%" }}>
          {etape.titre}
        </Box>
        <Box sx={{ fontSize:"14px", color:"text.primary", lineHeight:"150%", marginTop:"8px" }}>
          {etape.texte}
        </Box>
        <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:"16px" }}>
          <Button size="small" onClick={fermer}
            sx={{ textTransform:"none", color:"text.subTitle", paddingLeft:0 }}>
            {etape.passer || "Passer"}
          </Button>
          <Button size="small" variant="contained"
            onClick={() => { if (dernier) fermer(); else setPas(pas + 1) }}
            sx={{ textTransform:"none" }}>
            {dernier ? (etape.terminer || "J'ai compris") : (etape.suivant || "Suivant")}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default VisiteGuidee
